// What "refresh data" actually does, per provider, as the list of vendor calls it makes.
//
// The first version of this screen was a checklist of abstract preconditions, and it was the wrong thing: when
// a refresh produces nothing the question is never "is the provider registered", it is "what did it ask for
// and what came back". So the page is a call log — method, URL as sent, status, time, size — because every
// silent failure this codebase has had is legible in exactly those four columns and invisible without them.
import { useState } from "react";
import { Button, Card, Tag } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useSyncChain,
  useSyncChainRun,
  type SyncTraceRow,
  type SyncTraceStatus,
  type TracedCall,
  type SyncPreStep,
} from "../../app/hooks/useSyncChainDebug.js";
import styles from "./SyncChainPanel.module.scss";

const TONE: Record<SyncTraceStatus, "success" | "warning" | "danger" | "neutral"> = {
  ok: "success",
  warn: "warning",
  fail: "danger",
  skipped: "neutral",
  pending: "neutral",
};

/** Strip the origin so the path and its query — the part that differs and the part that breaks — lead. */
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

function Calls({ calls }: { calls: TracedCall[] }) {
  return (
    <ol className={styles.calls}>
      {calls.map((c, i) => (
        <li key={`${c.url}-${i}`} className={styles.call} data-ok={c.status !== null && c.status < 400}>
          <span className={styles.method}>{c.method}</span>
          {/* Full URL in the title: the query string is usually the diagnosis, and it is usually too long. */}
          <span className={styles.url} title={c.url}>
            {shortUrl(c.url)}
          </span>
          <span className={styles.status}>{c.status ?? c.outcome}</span>
          <span className={styles.ms}>{c.ms.toLocaleString()} ms</span>
          <span className={styles.bytes}>
            {c.bytes === null ? "—" : `${(c.bytes / 1024).toFixed(1)} KB`}
          </span>
        </li>
      ))}
    </ol>
  );
}

function TraceRow({ row, isRunning }: { row: SyncTraceRow; isRunning: boolean }) {
  const [open, setOpen] = useState(false);
  const hasCalls = row.calls.length > 0;
  // Expandable whenever there is anything to read. A failure BEFORE the first vendor call (a token the vault
  // cannot decrypt, a missing page id) has no calls — and its message is exactly the one the one-line summary
  // truncates, so a row gated on calls hid the only thing it had to say.
  const canOpen = hasCalls || !!row.summary;

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.rowHead}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        disabled={!canOpen}
        title={row.summary ?? undefined}
      >
        <span
          className={styles.light}
          data-status={isRunning ? "running" : row.status}
          aria-hidden="true"
        />
        <span className={styles.provider}>
          {row.provider}
          {row.kind ? <span className={styles.kind}>/{row.kind}</span> : null}
        </span>
        <span className={styles.summary}>{isRunning ? "running…" : row.summary}</span>
        {hasCalls ? (
          <Tag tone={TONE[row.status]} size="sm">
            {row.calls.length} call{row.calls.length === 1 ? "" : "s"} · {row.ms.toLocaleString()} ms
          </Tag>
        ) : null}
      </button>

      {open ? <p className={styles.summaryFull}>{row.summary}</p> : null}
      {open && hasCalls ? <Calls calls={row.calls} /> : null}
    </li>
  );
}

export function SyncChainPanel() {
  const chain = useSyncChain();
  const { run, results, running, preSteps, isRunning } = useSyncChainRun();

  if (chain.isPending) return <Card>Loading the sync trace…</Card>;
  if (chain.error) {
    return <Card>Could not load the sync trace: {errorMessage(chain.error, "the request failed")}</Card>;
  }

  const doc = chain.data;
  if (!doc) return null;

  // A run result supersedes the stored history for that provider; untouched rows keep theirs rather than
  // blanking, so a partial run still reads as a complete picture.
  const rows = doc.rows.map((r) => results[r.provider] ?? r);
  const providers = doc.rows.map((r) => r.provider);

  return (
    <Card>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Refresh trace</h2>
          <p className={styles.subtitle}>
            Every vendor call a refresh makes, as it is sent. Open a row for the URLs.
          </p>
        </div>
        <Button onClick={() => void run(providers)} disabled={isRunning || providers.length === 0}>
          {isRunning ? `Running ${running.size}…` : "Run refresh"}
        </Button>
      </div>

      {/* Where these calls actually go. Green against a stand-in and green against production are different
          claims, so the host is stated rather than implied. */}
      <p className={styles.vendor} data-fake={doc.vendorIsFake}>
        <code>{doc.vendorHost}</code> · env <code>{doc.envName}</code>
        {doc.vendorIsFake ? " — the local fake, so a run costs nothing" : " — the REAL vendor, so a run spends calls"}
      </p>

      {/* What the refresh does before touching a vendor. Shown first because it runs first, and because a
          band whose schedules never existed cannot be diagnosed by fetching. */}
      {preSteps.length > 0 ? (
        <ul className={styles.preSteps}>
          {preSteps.map((p: SyncPreStep) => (
            <li key={p.name} className={styles.preStep}>
              <span className={styles.light} data-status={p.status} aria-hidden="true" />
              <strong>{p.name}</strong>
              <span className={styles.summary}>{p.detail}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {rows.length === 0 ? (
        <p className={styles.empty}>
          This band has no sync schedules at all. If it is connected to anything, that is the bug — schedule
          birth has one entry point and a connect path that wires a subset of families misses it. “Refresh all
          data” in Settings › Connections self-heals it.
        </p>
      ) : (
        <ul className={styles.rows}>
          {rows.map((r) => (
            <TraceRow key={`${r.provider}/${r.kind}`} row={r} isRunning={running.has(r.provider)} />
          ))}
        </ul>
      )}

      {/* Named so their absence from the rows above is a stated fact rather than a gap someone tries to
          close (none today; the server keeps the list for when one exists). */}
      {doc.notSynced.length > 0 ? (
        <p className={styles.notSynced}>
          Outside the sync engine by design: {doc.notSynced.join("; ")}.
        </p>
      ) : null}
    </Card>
  );
}
